import React from "react";
import { useTranslation } from "react-i18next";
import SkeletonBlock from "./_shared/SkeletonBlock";

// SQL SUM() over zero rows comes back NULL, which the DataSet→JSON layer can
// serialize as {} rather than null — coerce through Number() so an empty
// aggregate reads as "no value" instead of leaking an object into the UI.
const numOf = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

const topItem = (list, labelKey, valueKey) => {
  if (!list?.length) return null;
  const sorted = [...list].sort(
    (a, b) => numOf(b[valueKey]) - numOf(a[valueKey])
  );
  const top = sorted[0];
  const topValue = top && numOf(top[valueKey]);
  if (!top || !topValue) return null;
  const total = list.reduce((sum, item) => sum + numOf(item[valueKey]), 0);
  const percentage = total > 0 ? ((topValue / total) * 100).toFixed(1) : 0;
  return { label: top[labelKey], value: topValue, percentage };
};

const sumValues = (list, valueKey) =>
  (list || []).reduce((sum, item) => sum + numOf(item[valueKey]), 0);

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

// Delta arrows come from backend Task 1613 (DiamondSummaryKPIs). Absent that
// table (older API, or a metric it doesn't cover), cards render the current
// value only — never a faked trend.
const trendFor = (summaryKPIs, metric) => {
  const row = (summaryKPIs || []).find((r) => r.Metric === metric);
  if (!row || !row.PriorValue) return null;
  const change = ((row.CurrentValue - row.PriorValue) / row.PriorValue) * 100;
  return { change, up: change >= 0 };
};

const ReportKpiBand = ({ report, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="edr-kpis">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonBlock className="edr-kpi-skeleton" key={index} />
        ))}
      </div>
    );
  }

  const totalClicks = report?.TotalDiamondClicks?.[0]?.TotalClickCnt || 0;
  const totalSearches = sumValues(report?.DiamondShapeSearches, "CNT");
  const topShape = topItem(report?.DiamondShapeSearches, "Cut", "CNT");
  const topColor = topItem(report?.DiamondColorSearches, "Color", "CNT");
  const topPrice = topItem(report?.DiamondPrice, "Range", "TOTAL");
  const topSize = topItem(report?.DiamondSizeSearches, "Size", "CNT");

  const clicksTrend = trendFor(report?.DiamondSummaryKPIs, "TotalClicks");
  const searchesTrend = trendFor(report?.DiamondSummaryKPIs, "TotalSearches");

  const kpis = [
    { lab: t("Total Clicks"), val: fmt(totalClicks), trend: clicksTrend },
    { lab: t("Diamond Searches"), val: fmt(totalSearches), trend: searchesTrend },
    {
      lab: t("Top Shape"),
      val: topShape?.label,
      sub: topShape && `${fmt(topShape.value)} ${t("searches")} · ${topShape.percentage}%`,
    },
    {
      lab: t("Top Price Band"),
      val: topPrice?.label,
      sub: topPrice && `${fmt(topPrice.value)} ${t("clicks")} · ${topPrice.percentage}%`,
    },
    {
      lab: t("Most Searched Size"),
      val: topSize?.label,
      sub: topSize && `${fmt(topSize.value)} · ${topSize.percentage}%`,
    },
    {
      lab: t("Top Color"),
      val: topColor?.label,
      sub: topColor && `${fmt(topColor.value)} · ${topColor.percentage}%`,
    },
  ];

  return (
    <div className="edr-kpis">
      {kpis.map((kpi, index) => (
        <div className="edr-kpi" key={index}>
          <div className="edr-kpi-lab">{kpi.lab}</div>
          <div className="edr-kpi-val">{kpi.val ?? "—"}</div>
          {kpi.sub && <div className="edr-kpi-sub">{kpi.sub}</div>}
          {kpi.trend && (
            <div className={`edr-kpi-trend ${kpi.trend.up ? "edr-up" : "edr-down"}`}>
              {kpi.trend.up ? "▲" : "▼"} {Math.abs(kpi.trend.change).toFixed(1)}%{" "}
              {t("vs prior period")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportKpiBand;
